import { type ComponentProps, type FC, memo, useMemo } from "react";
import dayjs from "dayjs";
import { cx } from "@stubhub/broadway-foundation/css";
import { Heading, LegacyTagSize, Tag, Text } from "@stubhub/broadway-web";
import { BaseballIcon, CarIcon, CheckIcon, NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import { EventGridListItemTags, useFormattedEventDateTime } from "@stubhub/ensemble-discovery";
import type { EventListGridItem } from "src/http/types";
import { Content, FormatContent, useContent, useFormattedContent } from "src/shared/components/content";
import { BaseDateCard } from "src/website-apps/responsive/stubhub-performer/components/calendar/BaseDateCard";
import { DAY_OF_MONTH_TBA } from "src/website-apps/responsive/stubhub-performer/components/calendar/constants";
import { useDateCardEventState } from "src/website-apps/responsive/stubhub-performer/components/calendar/hooks/useDateCardEventState";
import { NotificationOptionEnum, useStoredNotifications } from "src/website-apps/shared/components/notifications";
import { useTeamThemes } from "src/website-apps/shared/hooks/useTeamThemes";
import { calendarTagRecipe, calendarTagsRecipe, mlbDateCardFull, mlbDateCardHeadingPlayoff } from "./styles";

interface Props {
  /** Event being rendered. Must be a real event — caller branches on length first. */
  event: EventListGridItem;
  /** Event is a home game for the performer's primary team. */
  isHomeGame: boolean;
  /** Day of the month (1-31) to display in the card header. */
  dayOfMonth: number | null;
  /** Whether this card represents today's date. */
  today: boolean;
  /** Handler to open notification modal for sold-out events. */
  openNotificationHandler: (eventId: number) => void;
  /** Click event handler. */
  onClick: (event: EventListGridItem) => void;
  /** Mouse-enter event handler. */
  onMouseEnter: (event: EventListGridItem) => void;
  /** Position of this card amongst days-with-events in the calendar. */
  calendarIndex: number;
}

/**
 * Full-tile MLB date card — rendered when a day has exactly one event.
 * Owns the BaseDateCard wrapper, the heading/CTA slots, and the body
 * (opponent logo, vs/at line, time + home-away, parking/playoff details, merch tags).
 *
 * Sibling to MlbDateCardPortion: full-tile vs portion-of-tile for double-header days.
 * Per-event state (availability, merch tags, analytics, click handling, primary-team
 * theming) is owned by useDateCardEventState; this component handles MLB-specific
 * presentation only.
 */
export const MlbDateCardFull: FC<Props> = memo(
  ({ event, isHomeGame, dayOfMonth, today, openNotificationHandler, onClick, onMouseEnter, calendarIndex }) => {
    const isParkingEvent = !!event.isParkingEvent;
    const isPlayoffEvent = !!event.eventMetadata?.sports?.playoffSeriesMetadata;

    const { isNotificationStored } = useStoredNotifications();
    const isNotificationSignedUp = isNotificationStored(event.eventId, NotificationOptionEnum.EVENT);

    const {
      available,
      eventState,
      merchTags,
      inViewTrackers,
      primaryColor,
      handleClick,
      handleMouseEnter,
      handleInView,
    } = useDateCardEventState({
      event,
      isHomeGame,
      calendarIndex,
      openNotificationHandler,
      onClick,
      onMouseEnter,
    });
    const noTicketsAvailable = !available;

    const { formattedTime } = useFormattedEventDateTime(
      event.eventMetadata?.common?.eventStartDateTime,
      event.eventMetadata?.common?.eventEndDateTime ?? undefined,
      event.eventMetadata?.common?.venueTimeZoneOffset,
      event.onSaleDateTimeData?.onSaleDateTime
        ? dayjs(event.onSaleDateTimeData.onSaleDateTime).unix() * 1000
        : undefined,
      event.eventMetadata?.common?.passLength ?? undefined,
    );

    const teamThemes = useTeamThemes(event.eventMetadata);
    const secondaryTeamMetadataKey = isHomeGame
      ? event.eventMetadata?.sports?.awayTeamId
      : event.eventMetadata?.sports?.homeTeamId;
    const secondaryTeamMetadata = secondaryTeamMetadataKey
      ? event.eventMetadata?.metadataForAllCategoriesInEvent?.[secondaryTeamMetadataKey]
      : null;
    const secondaryTeamTheme = isHomeGame ? teamThemes.awayTeamData.theme : teamThemes.homeTeamData.theme;

    /** "\{0\} Logo" */
    const logoAltText = useFormattedContent("cid$#3561796#", [secondaryTeamMetadata?.shortName ?? "team"]);
    /** "TBA" */
    const timeTbaText = useContent("cid$#3860643#");
    /** "Sold out" */
    const soldOutText = useContent("cid$#3979713#");
    /** "Home" */
    const homeText = useContent("cid$#3979720#");
    /** "Away" */
    const awayText = useContent("cid$#3979719#");
    const homeAwayContent = isHomeGame ? homeText : awayText;

    // Slot recipes type slot classes as `string | undefined` because variants
    // _could_ omit a slot. Every slot here has a base style, so cast to a
    // dense record to avoid `?? ""` / `!` boilerplate at every callsite.
    const tileStyles = mlbDateCardFull({ ticketAvailability: noTicketsAvailable ? "soldOut" : "available" });
    const parkingTagClasses: ComponentProps<typeof Tag>["classes"] = { root: cx(tileStyles.parkingTag) };
    const tagsClasses = calendarTagsRecipe();
    const tagClasses = calendarTagRecipe();

    const heading = useMemo(() => {
      if (!isPlayoffEvent) {
        return null;
      }
      return (
        <Heading variant="title" size="small" as="h3" className={mlbDateCardHeadingPlayoff}>
          {event.eventMetadata?.sports?.playoffSeriesMetadata?.roundOfPlayoff}
        </Heading>
      );
    }, [isPlayoffEvent, event.eventMetadata?.sports?.playoffSeriesMetadata?.roundOfPlayoff]);

    // From-price is gated by TPerformerFromPriceInEventListV8 / MPerformerFromPriceInEventListV8
    // upstream — only populated when the user is in variant.
    const fromPrice = event.contextSensitiveFormattedMinPrice ?? null;

    const cta = useMemo(() => {
      if (available) {
        if (fromPrice) {
          return (
            <Heading variant="title" size="small" as="h3">
              {/* "From {0}" */}
              <FormatContent id="cid$#4099723#" params={[fromPrice]} />
            </Heading>
          );
        }
        return isParkingEvent ? (
          <Heading variant="title" size="small" as="h3">
            {/* "See passes" */}
            <Content id="cid$#3528590#" />
          </Heading>
        ) : (
          <Heading variant="title" size="small" as="h3">
            {/* "See tickets" */}
            <Content id="cid$#3562069#" />
          </Heading>
        );
      }
      return isNotificationSignedUp ? (
        <>
          <CheckIcon size={16} />
          <Text variant="label" size="small">
            {/* "On the list" */}
            <Content id="cid$#3994932#" />
          </Text>
        </>
      ) : (
        <>
          <NotificationIcon size={16} />
          <Text variant="label" size="small">
            {/* "Notify me" */}
            <Content id="cid$#3375073#" />
          </Text>
        </>
      );
    }, [available, fromPrice, isParkingEvent, isNotificationSignedUp]);

    return (
      <BaseDateCard
        event={event}
        eventState={eventState}
        eventURL={event.url ?? "#"}
        dayOfMonth={event.isDateConfirmed ? dayOfMonth : DAY_OF_MONTH_TBA}
        heading={heading}
        cta={cta}
        primaryColor={primaryColor}
        today={today}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onInView={handleInView}
        ariaLabel={event.name}
      >
        <div className={tileStyles.body}>
          <Text variant="label" size="small" as="div" className={tileStyles.headingLine}>
            {secondaryTeamTheme.transparentPartnerLogoUrl ? (
              <img
                className={tileStyles.logo}
                src={secondaryTeamTheme.transparentPartnerLogoUrl ?? undefined}
                alt={logoAltText}
                role="presentation"
              />
            ) : (
              <BaseballIcon size={40} />
            )}
            <div>
              {isHomeGame ? /* "vs" */ <Content id="cid$#3561795#" /> : /* "@" */ <Content id="cid$#3561783#" />}
              &nbsp;
              {secondaryTeamMetadata?.shortName ?? /* "TBD" */ <Content id="cid$#3983126#" />}
            </div>
          </Text>
          <Text variant="label" size="extra-small" className={tileStyles.subtitleLine}>
            {[
              event.isTimeConfirmed === false ? timeTbaText : formattedTime,
              noTicketsAvailable ? soldOutText : homeAwayContent,
            ]
              .filter(Boolean)
              .join(" ∙ ")}
          </Text>
          {isParkingEvent ? (
            <Tag classes={parkingTagClasses} startIcon={CarIcon} size={LegacyTagSize.Small}>
              {/* "Parking" */}
              <Content id="cid$#3987095#" />
            </Tag>
          ) : (
            isPlayoffEvent && (
              <Text variant="label" size="extra-small" className={tileStyles.subtitleLine}>
                {(event.eventMetadata?.sports?.playoffSeriesMetadata?.seriesGameNum ?? 0) > 0 ? (
                  // "Game {0}"
                  <FormatContent
                    id="cid$#3983231#"
                    params={[event.eventMetadata?.sports?.playoffSeriesMetadata?.seriesGameNum ?? "?"]}
                  />
                ) : (
                  // "Home Game {0}"
                  <FormatContent
                    id="cid$#3984357#"
                    params={[event.eventMetadata?.sports?.playoffSeriesMetadata?.homeGameNum ?? "?"]}
                  />
                )}
              </Text>
            )
          )}
          {merchTags.length > 0 && (
            <EventGridListItemTags legacy tags={merchTags} classes={tagsClasses} tagClasses={tagClasses} />
          )}
        </div>
        {inViewTrackers}
      </BaseDateCard>
    );
  },
);
MlbDateCardFull.displayName = "MlbDateCardFull";
